<template>
  <div>
    <div>
      <b-col class="d-flex justify-content-end px-0 pt-25 mb-2">
        <span>
          <b-dropdown
            ref="dateDropdown"
            menu-class="py-0 daterangepicker-menu"
            right
            class="mr-50 ml-0 ml-md-50 mb-50 mb-md-0"
            variant="outline-dark"
            size="sm"
            no-caret
          >
            <template #button-content>
              {{ dateRange }}
              <GjIcon
                name="Calendar"
              />
            </template>
            <div class="p-2">
              <date-range-picker
                :ranges="predefinedRanges"
                default-range-select="today"
                :allow-compare="false"
                @submit="submitDateRange"
                @cancel="closeDropdown"
              />
            </div>
          </b-dropdown>
        </span>
        <b-dropdown
          class="bg-white"
          variant="outline-dark"
          size="sm"
          right
        >
          <template #button-content>
            <GjIcon
              class="mr-50"
              name="Redirect"
            />
            {{ $tk('ClientPortal.MasterTable.ExportData') }}
            <b-spinner
              v-if="exporting"
              small
              class="ml-2"
              label="Exporting data"
            />
          </template>
          <b-dropdown-item @click="exportData('csv')">
            {{ $tk('ClientPortal.MasterTable.ExportDataAsCSV') }}
          </b-dropdown-item>
          <b-dropdown-item @click="exportData('xlsx')">
            {{ $tk('ClientPortal.MasterTable.ExportDataAsExcel') }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
    </div>
    <section>
      <b-card
        class="my-50 admin-table--management"
        no-body
      >
        <Table
          :data="allErrors"
          :columns="errorDashboardColumns"
          :is-loading="isLoading"
          class="admin-table__data"
          type-of-table="errordashboard"
          :enable-pagination="false"
          :enable-sort="false"
        />
      </b-card>
    </section>
  </div>
</template>

<script>

import Table from '@/components/Table.vue'
import {
  onMounted,
  ref,
  computed,
} from '@vue/composition-api'
import store from '@/store'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import moment from 'moment'
import DateRangePicker from '@/components/VueDateRangePicker'
import translation from '@/mixins/translate'
import ErrorDashboardService from '@/api/admin/errorDashboard.service'
import notification from '@/mixins/toastification'

export default {
  components: {
    Table,
    GjIcon,
    DateRangePicker,
  },
  mixins: [notification],

  setup(_, context) {
    const errorDashboardColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.ErrorDashboard.Code'),
        field: 'code',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.ErrorDashboard.Message'),
        field: 'message',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.ErrorDashboard.CountOfErrors'),
        field: 'errorCount',
      },
    ]
    const predefinedRanges = {
      today: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Today'),
        startDate: moment(),
        endDate: moment(),
      },
      yesterday: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Yesterday'),
        startDate: moment().subtract(1, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last7Days: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Last7Days'),
        startDate: moment().subtract(7, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last30Days: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Last30Days'),
        startDate: moment().subtract(30, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      lastMonth: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.LastMonth'),
        startDate: moment().subtract(1, 'months').startOf('month'),
        endDate: moment().subtract(1, 'month').endOf('month'),
      },
    }

    const allErrors = computed(() => store.getters.allErrors)
    const isLoading = ref(false)
    const exporting = ref(false)
    const dateRange = ref('')

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getErrors')
      dateRange.value = store.getters.getDateRange
      isLoading.value = false
    })

    const prependZero = value => {
      if (value.toString().length === 1) {
        return `0${value}`
      }
      return value
    }

    const formatDate = value => {
      const day = prependZero(value.getDate())
      const month = prependZero(value.getMonth() + 1)
      const year = prependZero(value.getFullYear())

      return `${day}/${month}/${year}`
    }

    const getDateFromMomentRange = (startMoment, endMoment) => {
      const startDate = new Date(startMoment)
      const endDate = new Date(endMoment)
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }

    const updateTable = async range => {
      isLoading.value = true
      try {
        const response = await ErrorDashboardService.getErrorsWithDateRange(range)
        if (response) {
          isLoading.value = false
        } else {
          notification.methods.error(translation.methods.$tk('Toast.ErrorFetchingImaErrors'))
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        await store.dispatch('getErrors', range)
      }
    }

    const closeDropdown = () => {
      context.refs.dateDropdown.hide(true)
    }

    const submitDateRange = range => {
      const { startDate, endDate } = range
      dateRange.value = getDateFromMomentRange(startDate, endDate)
      updateTable(dateRange.value)
      closeDropdown()
    }

    const createDownloadLink = (file, extension, range) => {
      const name = `Ima Errors Report - ${range}.${extension}`
      const url = window.URL.createObjectURL(new Blob([file]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', name)
      document.body.appendChild(link)
      link.click()
    }

    const exportData = async exportType => {
      exporting.value = true
      let response
      try {
        if (exportType === 'csv') {
          response = await ErrorDashboardService.exportCsv(dateRange.value)
        } else {
          response = await ErrorDashboardService.exportExcel(dateRange.value)
        }
        createDownloadLink(response, exportType, dateRange.value)
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        exporting.value = false
      }
    }
    return {
      allErrors,
      isLoading,
      predefinedRanges,
      submitDateRange,
      updateTable,
      closeDropdown,
      exportData,
      exporting,
      dateRange,
      errorDashboardColumns,
    }
  },
}

</script>
